import React, { useState } from 'react';
import { CircularProgress, Fade, MenuItem, Paper, Popper } from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { ReactComponent as WarningRedIcon } from '@src/assets/icons/waning-red.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { EXTRACTION_STATUS } from '@src/constants/project';

import Tooltip from '@src/components/Tooltip';
import sanitize from 'sanitize-html';
import { StyledIconButton } from './index.style';

const Actions = ({
  handleOpenDeleteDialog,
  extractionStatus,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const [anchorBlockOption, setAnchorBlockOption] = useState(null);
  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);

  const refClickOutSide = useClickOutside(() => {
    setOpenPopperBlockOption(false);
  });

  const handleTogglePopper = (e) => {
    e.stopPropagation();
    setOpenPopperBlockOption(!openPopperBlockOption);
    setAnchorBlockOption(e.currentTarget);
  };

  const moreVertButton = (
    <StyledIconButton
      data-id="view-other-project-actions"
      height="40px"
      width="40px"
      padding="8px"
      onClick={handleTogglePopper}
    >
      <MoreVertOutlinedIcon
        className="icon-morevert"
        color="rgba(30, 22, 78, 0.64)"
      />
    </StyledIconButton>
  );

  const popperMenu = (
    <Popper
      transition
      style={{ zIndex: 1000, width: '180px', color: '#1E164E' }}
      anchorEl={anchorBlockOption}
      open={openPopperBlockOption}
      placement="bottom-start"
    >
      {({ TransitionProps }) => (
        <Fade
          style={{
            padding: '4px 0px',
            boxShadow: '0px 0px 20px 2px rgba(0, 0, 0, 0.1)',
          }}
          {...TransitionProps}
          timeout={350}
        >
          <Paper>
            <MenuItem
              key="handleOpenDeleteBlock"
              style={{
                color: '#1E164E',
                fontSize: '14px',
                height: '40px',
                boxSizing: 'border-box',
                padding: '10px 16px',
                fontWeight: 500,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog();
              }}
            >
              <DeleteIcon style={{ color: '#FF3B30', fontSize: '24px' }} />
              <span>{t('deleteProject')}</span>
            </MenuItem>
          </Paper>
        </Fade>
      )}
    </Popper>
  );

  const renderStatusIcon = () => {
    if (extractionStatus === EXTRACTION_STATUS.PENDING) {
      return (
        <StyledIconButton
          height="40px"
          width="40px"
          padding="8px"
          isDisableHover
          onClick={(e) => e.stopPropagation()}
        >
          <CircularProgress size={24} sx={{ color: '#1E90FF' }} />
        </StyledIconButton>
      );
    }

    if (extractionStatus === EXTRACTION_STATUS.FAILURE) {
      return (
        <Tooltip
          title={
            <span
              style={{
                fontSize: 14,
                textAlign: 'start',
                display: 'flex',
                flexDirection: 'column',
              }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitize(errorMessage || t('extractionFailed')),
              }}
            />
          }
          placement="left"
          arrow
          componentsProps={{
            tooltip: { style: { padding: '8px 16px' } },
          }}
        >
          <StyledIconButton
            height="40px"
            width="40px"
            padding="8px"
            onClick={(e) => e.stopPropagation()}
          >
            <WarningRedIcon width={24} height={24} />
          </StyledIconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const statusIcon = renderStatusIcon();

  return (
    <>
      <div
        ref={refClickOutSide}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        {statusIcon}
        {moreVertButton}
      </div>
      {popperMenu}
    </>
  );
};

export default Actions;
